import React, { useState, useRef, useEffect } from 'react';
import {
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { IOnboardingTemplateType } from 'core/interfaces/onboardingInterface';
import { uploadOnboardingDocumentFile } from 'core/API/onboarding';
import styles from '../SchedulerSetting.module.scss';

interface UploadDocumentProps {
  existingDocumentsCount: number;
  existingNames: string[];
  templateTypes: IOnboardingTemplateType[];
  handleBackClick?: () => void;
  onDocumentUploaded?: () => void;
}

const UploadDocument: React.FC<UploadDocumentProps> = ({
  existingDocumentsCount,
  existingNames,
  templateTypes,
  handleBackClick,
  onDocumentUploaded,
}) => {
  const [name, setName] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isManagerSignatureRequired, setIsManagerSignatureRequired] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeTemplateTypes = templateTypes.filter((t) => t.active !== false);

  useEffect(() => {
    // Set default type when templateTypes are available
    if (activeTemplateTypes.length > 0 && !selectedTypeId) {
      setSelectedTypeId(activeTemplateTypes[0].id);
    }
  }, [activeTemplateTypes, selectedTypeId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        setSelectedFile(null);
        return;
      }

      // Validate file size (e.g., max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setError('File size must be less than 10MB');
        setSelectedFile(null);
        return;
      }

      // Check for duplicate file name
      if (existingNames.includes(file.name)) {
        setError(`A document named "${file.name}" already exists. Names must be unique.`);
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);
      
      // Auto-populate name from file name if empty
      if (!name) {
        const nameWithoutExt = file.name.replace(/\.pdf$/i, '');
        setName(nameWithoutExt);
      }
    }
  };

  const handleDocumentTypeChange = (event: SelectChangeEvent<number>) => {
    setSelectedTypeId(Number(event.target.value));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!selectedFile) {
      setError('Please select a PDF file to upload');
      return;
    }

    if (existingDocumentsCount >= 50) {
      setError('Maximum of 50 documents reached');
      return;
    }

    if (!selectedTypeId) {
      setError('Document type is required');
      return;
    }

    setIsUploading(true);

    try {
      // Next sort order is the count + 1
      const nextSortOrder = existingDocumentsCount + 1;

      await uploadOnboardingDocumentFile(
        selectedFile,
        name.trim(),
        selectedTypeId,
        nextSortOrder,
        isManagerSignatureRequired
      );

      onDocumentUploaded?.();
      setTimeout(() => {
        handleBackClick?.();
      }, 500);
    } catch (err: any) {
      setError(err?.message || 'Failed to upload document');
      setIsUploading(false);
    }
  };

  return (
    <div className={styles.editGeneralSettings}>
      <Grid container direction="column" spacing={2} pl={2}>
        <Grid container direction="row" justifyContent="space-between">
          <Button className={styles.backButton} onClick={handleBackClick} variant="contained">
            Back
          </Button>
          <Button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={isUploading || !selectedFile}
            variant="contained"
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Grid>
        <Grid>
          <h1 className={styles.formTitle}>Upload Onboarding Document</h1>
        </Grid>
      </Grid>

      <form className={styles.formContainer} onSubmit={handleSubmit}>
        {error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

        <div style={{ marginBottom: 16, color: '#666', fontSize: '0.875rem' }}>
          {existingDocumentsCount} of 50 documents uploaded
        </div>

        <div
          style={{
            border: selectedFile ? '2px dashed #4caf50' : '2px dashed #ccc',
            borderRadius: 8,
            padding: 24,
            textAlign: 'center',
            marginBottom: 16,
            cursor: 'pointer',
            backgroundColor: '#fafafa',
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <CloudUpload style={{ fontSize: 48, color: '#999', marginBottom: 8 }} />
          <div style={{ marginBottom: 4 }}>
            {selectedFile ? selectedFile.name : 'Click to select a PDF file'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#999' }}>
            Maximum file size: 10MB | Format: PDF only
          </div>
        </div>

        <TextField
          label="Name"
          name="name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          className={styles.input}
          margin="normal"
          fullWidth
          required
          helperText="Document name shown to employees"
          inputProps={{
            maxLength: 100,
          }}
        />

        <FormControl fullWidth margin="normal" required className={styles.input}>
          <InputLabel>Document Type</InputLabel>
          <Select
            value={selectedTypeId || ''}
            onChange={handleDocumentTypeChange}
            label="Document Type"
            disabled={isUploading}
          >
            {activeTemplateTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div style={{ color: '#666', marginTop: 8, fontSize: '0.875rem' }}>
          Select the type of PDF document
        </div>

        <FormControlLabel
          control={
            <Checkbox
              checked={isManagerSignatureRequired}
              onChange={(e) => setIsManagerSignatureRequired(e.target.checked)}
              disabled={isUploading}
            />
          }
          label="Require Manager Signature"
          style={{ marginTop: 16 }}
        />
        <div style={{ color: '#666', fontSize: '0.875rem', marginLeft: 32 }}>
          Check this if manager signature is required for this document
        </div>
      </form>
    </div>
  );
};

export default UploadDocument;
